/* eslint max-lines: ["error", {"max": 300, "skipComments": true}] */
import { Dispatch } from 'redux';
import axios, { CancelTokenSource } from 'axios';
import { getSearchQueryParamsFromState, buildSearchUrl } from '../utils';
import actions from '../../../state/actions';
import { reportSearch } from './searchReport';
import { shouldReportError } from './errorsUtils';
import {
  ajaxSearch,
  ajaxSearchCount,
  checkIfWatcherAlreadyExists,
  ErrorType,
} from '../search.api';

import {
  SYNC_QUERY_RESPONSE,
  SEARCH_REQUEST,
  PAGINATION_REQUEST,
  SYNC_PAGINATION_RESPONSE,
  SYNC_COUNTS,
  SEARCH_ERROR,
  CANCEL_REQUEST,
  PAGINATION_ERROR,
  CANCEL_PAGINATION_REQUEST,
} from '../action-types';
import { doctrineAnalytics } from '../../../utils/analyticsUtils';
import { setWatcherExists } from './watcherExists';
import { StateType } from '../../../state/state.types';
import {
  SearchParams,
  SearchAPIResponse,
  SearchAPICount,
} from '../../search.types';

let cancelSearch: CancelTokenSource;

let cancelPagination: CancelTokenSource;

let cancelCount: CancelTokenSource;
let cancelWatcherExistsRequest: CancelTokenSource;

const syncQueryResponse = (
  response: SearchAPIResponse,
  queryParams: StateType['queryParams'],
) =>
  <const>{
    type: SYNC_QUERY_RESPONSE,
    payload: {
      queryParams,
      response,
    },
  };

const syncPaginationResponse = (response: SearchAPIResponse) =>
  <const>{
    type: SYNC_PAGINATION_RESPONSE,
    payload: {
      response,
    },
  };

const searchRequest = () =>
  <const>{
    type: SEARCH_REQUEST,
  };

const paginationRequest = () =>
  <const>{
    type: PAGINATION_REQUEST,
  };

const syncCounts = (counts: SearchAPICount) =>
  <const>{
    type: SYNC_COUNTS,
    payload: {
      counts,
    },
  };

const searchError = (data: ErrorType) =>
  <const>{
    type: SEARCH_ERROR,
    payload: {
      data,
    },
  };

const paginationError = (data: ErrorType) =>
  <const>{
    type: PAGINATION_ERROR,
    payload: {
      data,
    },
  };
const cancelPaginationRequest = () =>
  <const>{
    type: CANCEL_PAGINATION_REQUEST,
  };

const cancelRequest = () =>
  <const>{
    type: CANCEL_REQUEST,
  };

export const search = ({ reason }: { reason: string }) => (
  dispatch: Dispatch,
  getState: () => StateType,
) => {
  dispatch(searchRequest());

  if (cancelPagination) {
    cancelPagination.cancel();
  }

  if (cancelSearch) {
    cancelSearch.cancel();
  }
  const state = getState();

  const startRequestTime = new Date().getTime();

  const searchURL = buildSearchUrl(state.queryParams);

  if (!['typed', 'browser_navigation', 'original_query'].includes(reason)) {
    window.history.pushState(null, 'Résultats'[0], searchURL);
  }

  const params = getSearchQueryParamsFromState(state.queryParams);

  const [promise, cancelTokenSource] = ajaxSearch(params);

  cancelSearch = cancelTokenSource;

  promise
    .then(response => {
      if (!response.data.error) {
        const { data } = response;

        reportSearch(data, params, {
          startRequestTime,
          reason,
          user: state.user,
        });

        if (
          data.aggregations &&
          data.aggregations.dates &&
          !data.aggregations.dates.length
        ) {
          // dates are handled very badly by search
          // dates not empty only if we have "in YEAR" in the query
          delete data.aggregations.dates;
        }

        dispatch(syncQueryResponse(data, state.queryParams));
      } else {
        // request too long or imprecise
        const thrown = { response };
        throw thrown;
      }
    })
    .catch(thrown => {
      if (!axios.isCancel(thrown)) {
        if (thrown.response) {
          const { data } = thrown.response;
          dispatch(searchError(data));
          if (data && data.error === 'SEARCH_QUOTA_EXCEEDED') {
            dispatch(actions.user.setQuotaExceeded());
            doctrineAnalytics('Search LimitAppeared');
          } else if (data && data.error === 'TRIAL_EXPIRED') {
            dispatch(actions.user.setTrialExpired());
            doctrineAnalytics('Account TrialExpiredAppeared', {
              from: 'search',
            });
          } else if (
            data &&
            data.error === 'CANCELLED_BECAUSE_OF_NEWER_QUERY' &&
            window.Rollbar
          ) {
            window.Rollbar.error(
              'User received CANCELLED_BECAUSE_OF_NEWER_QUERY error during scroll',
            );
          }
        } else {
          throw thrown;
        }
      } else {
        dispatch(cancelRequest());
      }
    });

  getSearchCount(params)
    .then(response => {
      dispatch(syncCounts(response.data));
    })
    .catch(thrown => {
      if (shouldReportError(thrown)) {
        throw thrown;
      }
    });

  checkIfWatcherExists(params)
    .then(response => {
      dispatch(setWatcherExists(response.data));
    })
    .catch(error => {
      if (!axios.isCancel(error)) {
        throw error;
      }
    });
};

export const searchNext = () => (
  dispatch: Dispatch,
  getState: () => StateType,
) => {
  dispatch(paginationRequest());
  const state = getState();
  const params = getSearchQueryParamsFromState(state.queryParams);

  const { query, autoCorrectedQuery } = state.results;
  params.q = autoCorrectedQuery || query;

  const [promise, cancelTokenSource] = ajaxSearch(params);

  cancelPagination = cancelTokenSource;

  promise
    .then(response => {
      dispatch(syncPaginationResponse(response.data));
    })
    .catch(thrown => {
      if (!axios.isCancel(thrown)) {
        if (thrown.response) {
          const { data } = thrown.response;
          dispatch(paginationError(data));
          if (data && data.error === 'SEARCH_QUOTA_EXCEEDED') {
            dispatch(actions.user.setQuotaExceeded());
            doctrineAnalytics('Search LimitAppeared');
          } else if (data && data.error === 'TRIAL_EXPIRED') {
            dispatch(actions.user.setTrialExpired());
            doctrineAnalytics('Account TrialExpiredAppeared', {
              from: 'pagination',
            });
          } else {
            throw thrown;
          }
        } else {
          throw thrown;
        }
      } else {
        dispatch(cancelPaginationRequest());
      }
    });
};

function getSearchCount(params: SearchParams) {
  if (cancelCount) {
    cancelCount.cancel();
  }

  const [promise, cancelTokenSource] = ajaxSearchCount(params);
  cancelCount = cancelTokenSource;
  return promise;
}

function checkIfWatcherExists(params: SearchParams) {
  if (cancelWatcherExistsRequest) {
    cancelWatcherExistsRequest.cancel();
  }

  const [promise, cancelTokenSource] = checkIfWatcherAlreadyExists(params);

  cancelWatcherExistsRequest = cancelTokenSource;

  return promise;
}

export type SearchActions = ReturnType<
  | typeof syncQueryResponse
  | typeof syncPaginationResponse
  | typeof searchRequest
  | typeof paginationRequest
  | typeof syncCounts
  | typeof searchError
  | typeof paginationError
  | typeof cancelPaginationRequest
  | typeof cancelRequest
>;
